import { useQuery } from '@tanstack/react-query'
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useOrg } from './org'
import { useSetting } from './settings'
import { useTRPC } from './trpc'

/**
 * Which orgs a view shows: the one being worked in, or the whole family of
 * orgs linked under it.
 */
export type OrgScope = 'org' | 'family'

/**
 * The parents a person has turned to All orgs, by space id. A parent absent
 * from it is on This org, which is where every parent opens.
 */
type Scopes = Record<string, 'family'>

const SCOPE_SETTING = 'org.scope'

/** A stored value is read rather than trusted: anything else is This org. */
function readScopes(raw: unknown): Scopes {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const scopes: Scopes = {}
  for (const [spaceId, value] of Object.entries(raw)) {
    if (value === 'family') scopes[spaceId] = 'family'
  }
  return scopes
}

type OrgScopeValue = {
  scope: OrgScope
  /**
   * Whether the scope is the settled answer: the org's links and the person's
   * stored choice have both arrived. A view reads nothing before it, so a parent
   * left on All orgs never shows its own roster first and the family's after.
   */
  ready: boolean
  setScope: (next: OrgScope) => void
  /** The org has orgs linked under it, so there is a family to show. */
  linked: boolean
  /** How many views on screen read the scope. */
  readers: number
  join: () => () => void
}

const OrgScopeContext = createContext<OrgScopeValue | null>(null)

/**
 * One scope for every view that can combine a family of linked orgs.
 *
 * App-level for the same reason the org is: a parent's office asks "this org
 * or all of them" once, and every view able to answer follows. It is the
 * person's own setting, held against their account (`useSetting`), per parent —
 * so it is remembered wherever they sign in, and turning one parent to All
 * orgs leaves every other parent on This org.
 *
 * There is a family only where the org has orgs linked under it; anywhere else
 * the scope is This org whatever was stored. `active` is whether the surface is
 * an org's at all: elsewhere nothing is asked, and the scope is This org.
 */
export function OrgScopeProvider({
  active,
  children,
}: {
  active: boolean
  children: ReactNode
}) {
  const trpc = useTRPC()
  const { spaceId } = useOrg()
  const asking = active && !!spaceId
  /* The same read the org's settings make, so a cached view answers it. */
  const view = useQuery({
    ...trpc.org.get.queryOptions({ spaceId: spaceId ?? '' }),
    enabled: asking,
  })
  const linked = asking && (view.data?.children?.length ?? 0) > 0
  const [scopes, setScopes, settled] = useSetting(SCOPE_SETTING, readScopes)
  const [readers, setReaders] = useState(0)

  const ready = !asking || (view.isFetched && (!linked || settled))
  const scope: OrgScope =
    linked && spaceId && scopes[spaceId] === 'family' ? 'family' : 'org'

  const setScope = useCallback(
    (next: OrgScope) => {
      if (!spaceId) return
      setScopes((current) => {
        const { [spaceId]: _held, ...rest } = current
        return next === 'family' ? { ...rest, [spaceId]: 'family' } : rest
      })
    },
    [spaceId, setScopes],
  )

  const join = useCallback(() => {
    setReaders((n) => n + 1)
    return () => setReaders((n) => n - 1)
  }, [])

  /* One value while nothing in it changes. The shell re-renders whenever a
     page declares an action, and a fresh value each time re-renders every
     view reading the scope, whose actions then declare themselves again. */
  const value = useMemo(
    () => ({ scope, ready, setScope, linked, readers, join }),
    [scope, ready, setScope, linked, readers, join],
  )

  return (
    <OrgScopeContext.Provider value={value}>
      {children}
    </OrgScopeContext.Provider>
  )
}

/**
 * The scope, for a view that can combine a family: calling it is what
 * declares the view one, so the chrome offers the toggle while it is on
 * screen and nowhere else. The view waits for `ready` before reading.
 */
export function useOrgScope(): { scope: OrgScope; ready: boolean } {
  const ctx = useContext(OrgScopeContext)
  const join = ctx?.join
  useEffect(() => join?.(), [join])
  return { scope: ctx?.scope ?? 'org', ready: ctx?.ready ?? true }
}

/**
 * The scope as it stands, read WITHOUT declaring the view one that combines.
 *
 * For a page that combines only for some readers: it reads the scope here,
 * and declares itself with `CombinesScope` only where the reader can see what
 * the scope changes, so nobody is offered a toggle that does nothing for them.
 */
export function useOrgScopeReading(): OrgScope {
  return useContext(OrgScopeContext)?.scope ?? 'org'
}

/** Declares the view on screen one that combines, for as long as it is drawn. */
export function CombinesScope() {
  useOrgScope()
  return null
}

/**
 * The toggle's side, for the chrome that draws it: shown only where the org
 * has a family and a view on screen reads the scope.
 */
export function useOrgScopeControl(): {
  shown: boolean
  scope: OrgScope
  setScope: (next: OrgScope) => void
} {
  const ctx = useContext(OrgScopeContext)
  return {
    shown: !!ctx && ctx.linked && ctx.readers > 0,
    scope: ctx?.scope ?? 'org',
    setScope: ctx?.setScope ?? (() => {}),
  }
}
